import React, { useMemo } from 'react';
import cn from 'classnames';
import { useField } from 'informed';

import { HelperTextStyledComponent } from '@nw/shared/ui';

import {
  TileFieldContainer,
  TileOptionButton,
  TileOptionsRow,
  TileSelectErrorText
} from './styledComponents';
import {
  resolveTileUiTheme,
  TileUiThemeConfig
} from './resolveTileUiTheme';

export type { ResolvedTileUiTheme, TileUiThemeConfig } from './resolveTileUiTheme';
export {
  DEFAULT_TILE_UI_THEME,
  parseConfigHexColor,
  resolveTileUiTheme
} from './resolveTileUiTheme';

export interface InformedTileSelectFieldProps {
  label: string;
  field: string;
  error: string;
  touched: boolean;
  isDisabled: boolean;
  validationSchemaObject: any;
  options: { label: string; value: string }[];
  /** Optional per-field color overrides when `options_UI` is TILE_UI; omit for default Figma theme. */
  tileUiTheme?: TileUiThemeConfig | null;
  helperText?: string;
  isHidden?: boolean;
  reRenderObjects?: Record<string, unknown>[];
  onChangeOption?: (value: string) => void;
  validateOnBlur?: boolean;
  labelStyles?: string;
  isMarginBottomZero?: boolean;
  errorMessageStyles?: string;
  handleFocus?: () => void;
}

const InformedTileSelectField = (props: InformedTileSelectFieldProps) => {
  const {
    label,
    field,
    error,
    touched,
    isDisabled,
    validationSchemaObject,
    options,
    tileUiTheme,
    helperText,
    isHidden,
    reRenderObjects,
    onChangeOption,
    validateOnBlur = true,
    labelStyles,
    isMarginBottomZero,
    errorMessageStyles,
    handleFocus
  } = props;

  const tileColors = useMemo(
    () => resolveTileUiTheme(tileUiTheme),
    [tileUiTheme]
  );

  const { fieldState, render, informed, fieldApi } = useField({
    field,
    validateOnBlur,
    validateOnChange: true,
    validationSchema: validationSchemaObject.fields[field],
    error,
    touched,
    isDisabled,
    label,
    ...(reRenderObjects ?? [])
  });

  const { value } = fieldState;
  const { onChange, onBlur } = informed;
  const { setValue, setTouched } = fieldApi;

  const showError = Boolean(error && touched);
  const currentValue =
    value === undefined || value === null ? '' : String(value);

  const handleGroupBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      onBlur(e);
    }
  };

  const renderHelperText = () => {
    return helperText && !error ? (
      <HelperTextStyledComponent helperText={helperText} />
    ) : null;
  };

  return render(
    <TileFieldContainer isHidden={isHidden} isMarginBottomZero={isMarginBottomZero}>
      <div
        className={cn('w-full')}
        role="group"
        aria-label={label || field}
        onBlur={handleGroupBlur}
      >
        {label ? <div className={cn(labelStyles)}>{label}</div> : null}
        <TileOptionsRow>
          {(options ?? []).map((option) => {
            const isSelected = currentValue === String(option.value);
            return (
              <TileOptionButton
                key={String(option.value)}
                type="button"
                isSelected={isSelected}
                disabled={isDisabled}
                aria-pressed={isSelected}
                $selectedBg={tileColors.selectedBackground}
                $selectedBorder={tileColors.selectedBorder}
                $defaultBg={tileColors.defaultBackground}
                $defaultBorder={tileColors.defaultBorder}
                $textColor={tileColors.textColor}
                onFocus={() => handleFocus?.()}
                onClick={() => {
                  if (isDisabled) return;
                  const next = String(option.value);
                  setValue(next);
                  onChange(next);
                  setTouched(true);
                  onChangeOption?.(next);
                }}
              >
                {option.label}
              </TileOptionButton>
            );
          })}
        </TileOptionsRow>
      </div>
      <TileSelectErrorText className={cn(errorMessageStyles)}>
        {showError ? error : '\u00a0'}
      </TileSelectErrorText>
      {renderHelperText()}
    </TileFieldContainer>
  );
};

export { InformedTileSelectField };
